import { useCallback, useEffect } from 'react'
import {
  useMidiStore,
  VIRTUAL_INPUTS,
  VIRTUAL_SEQUENCER_INPUT_ID,
  isVirtualDestination,
  type HwMidiRoute,
  type MidiDevice,
} from '@/stores/midiStore'
import { parseMidiMessage, type MidiMessage } from '@/lib/midiUtils'
import { useEffectiveMidi } from '@/hooks/useEffectiveMidi'

/**
 * Callback signature for components that want incoming note-ons
 * routed to a virtual destination. Listener receives the parsed
 * note, velocity (0..1 normalized), and MIDI channel (1..16). For
 * note-off events the listener gets velocity=0.
 */
export type SequencerNoteInListener = (event: {
  note: number
  velocity: number
  channel: number
  isNoteOn: boolean
}) => void

/**
 * Module-level listener slot. Kept as an array so multiple
 * components can subscribe (edge case — today only SequencerPage
 * needs it) without a provider wrapper.
 */
const sequencerNoteInListeners = new Set<SequencerNoteInListener>()

function emitSequencerNoteIn(event: Parameters<SequencerNoteInListener>[0]) {
  for (const l of sequencerNoteInListeners) l(event)
}

/**
 * Callback signature for the app-wide mapping dispatcher. Unlike
 * `SequencerNoteInListener`, this sees *every* inbound message kind
 * (note-on/off, CC, pitchbend, program), with the raw parsed data
 * the MIDI mapping registry needs to match bindings. Gating ("is
 * forwarding enabled?") happens inside the listener — the dispatch
 * call is unconditional so the listener can react to the flag
 * flipping without us having to re-attach on every change.
 */
export type GlobalMidiInListener = (event: {
  message: MidiMessage
  inputId: string
}) => void

const globalMidiInListeners = new Set<GlobalMidiInListener>()

function emitGlobalMidiIn(event: Parameters<GlobalMidiInListener>[0]) {
  for (const l of globalMidiInListeners) l(event)
}

/**
 * Decode a raw MIDI status+data-byte triplet into a note event and
 * forward it to registered sequencer listeners. Handles both
 * note-on (0x90) and note-off (0x80) plus running-status "note-on
 * with velocity 0" which DAWs treat as note-off.
 */
function dispatchSequencerNoteIn(data: number[]) {
  if (data.length < 3) return
  const status = data[0] & 0xf0
  const channel = (data[0] & 0x0f) + 1
  const note = data[1] & 0x7f
  const rawVel = data[2] & 0x7f
  if (status === 0x90 && rawVel > 0) {
    emitSequencerNoteIn({
      note,
      velocity: rawVel / 127,
      channel,
      isNoteOn: true,
    })
  } else if (status === 0x80 || (status === 0x90 && rawVel === 0)) {
    emitSequencerNoteIn({ note, velocity: 0, channel, isNoteOn: false })
  }
  // Other status bytes (CC, pitchbend) are ignored for pad triggering.
}

/**
 * Web MIDI permits exactly one `MIDIAccess` per page, and every
 * component that calls `useWebMidi()` (MidiRouter requesting access,
 * PianoKeyboard dispatching virtual notes) needs to see the same
 * outputs map and the same routing table. Per-hook-instance refs
 * silently dropped sends because the keyboard's instance had a
 * different `accessRef.current === null` than the router's. So we
 * keep both as module-level singletons.
 */
let sharedAccess: MIDIAccess | null = null
const sharedRoutesRef: { current: HwMidiRoute[] } = { current: [] }
const sharedSelectedInputRef: { current: string | null } = { current: null }
const attachedInputs = new WeakSet<MIDIInput>()
let accessSetupPromise: Promise<void> | null = null

function enumerateDevices(access: MIDIAccess): {
  inputs: MidiDevice[]
  outputs: MidiDevice[]
} {
  const inputs: MidiDevice[] = []
  const outputs: MidiDevice[] = []
  access.inputs.forEach(input => {
    inputs.push({
      id: input.id,
      name: input.name ?? 'Unknown Input',
      manufacturer: input.manufacturer ?? '',
      state: input.state,
    })
  })
  access.outputs.forEach(output => {
    outputs.push({
      id: output.id,
      name: output.name ?? 'Unknown Output',
      manufacturer: output.manufacturer ?? '',
      state: output.state,
    })
  })
  return { inputs, outputs }
}

/**
 * Attach the routing+monitor handler to a hardware input, but only
 * once per input across the whole page. Subsequent calls (after
 * hotplug refresh) are no-ops, which avoids `onmidimessage` flapping
 * and keeps the closure stable.
 */
function ensureInputHandler(
  input: MIDIInput,
  pushMessage: (msg: MidiMessage) => void,
) {
  if (attachedInputs.has(input)) return
  attachedInputs.add(input)
  const inputName = input.name ?? 'Unknown Input'
  input.onmidimessage = (event: MIDIMessageEvent) => {
    if (!event.data) return
    const data = Array.from(event.data)

    // Forward to all routed destinations. Hardware outputs go
    // through `access.outputs`; virtual destinations (today just
    // the Sequencer) dispatch into their registered listeners.
    if (sharedAccess) {
      for (const route of sharedRoutesRef.current) {
        if (route.inputId !== input.id) continue
        if (isVirtualDestination(route.outputId)) {
          if (route.outputId === VIRTUAL_SEQUENCER_INPUT_ID) {
            dispatchSequencerNoteIn(data)
          }
          continue
        }
        sharedAccess.outputs.get(route.outputId)?.send(data)
      }
    }

    // Broadcast every inbound message to the global mapping
    // dispatcher, regardless of routing. Listeners (GlobalMidiProvider)
    // decide whether to act based on midiForwardingEnabled and their
    // mapping table. Parsing once here avoids each listener having
    // to decode the raw bytes.
    const parsedForGlobal = parseMidiMessage(event.data, event.timeStamp)
    emitGlobalMidiIn({
      message: { ...parsedForGlobal, inputId: input.id, inputName },
      inputId: input.id,
    })

    // Push to the monitor only when this input is the focused one,
    // so the log doesn't drown in traffic from every connected device.
    if (sharedSelectedInputRef.current === input.id) {
      pushMessage({ ...parsedForGlobal, inputId: input.id, inputName })
    }
  }
}

/**
 * Hook wrapping the Web MIDI API. Call from any component that needs
 * to either request access (MidiRouter does this on mount) or dispatch
 * a virtual MIDI message (PianoKeyboard does this on note events).
 *
 * Once access is granted the singleton subscribes to every hardware
 * input. Each incoming message is fanned out to the user's routed
 * outputs and pushed to the monitor log tagged with its source.
 */
export function useWebMidi() {
  const setSupported = useMidiStore(s => s.setSupported)
  const setHwDevices = useMidiStore(s => s.setHwDevices)
  const pushMessage = useMidiStore(s => s.pushMessage)
  const selectedHwInputId = useMidiStore(s => s.selectedHwInputId)
  // Use the effective MIDI layer so the dispatcher honours project
  // overrides when a project is active. When no project is loaded
  // (or the active project hasn't populated a midiConfig override),
  // `effective.routes` is identical to `midiStore.routes`, so this
  // is a drop-in upgrade — the `/midi` page with no project behaves
  // exactly as before.
  const effective = useEffectiveMidi()
  const routes = effective.routes

  // Mirror routes + the monitor's focused input into the shared
  // module-level refs so the singleton handler picks up changes
  // without re-attachment.
  useEffect(() => {
    sharedRoutesRef.current = routes
  }, [routes])
  useEffect(() => {
    sharedSelectedInputRef.current = selectedHwInputId
  }, [selectedHwInputId])

  const requestAccess = useCallback(async () => {
    if (sharedAccess) return
    if (!navigator.requestMIDIAccess) {
      setSupported(false)
      return
    }
    if (accessSetupPromise) {
      await accessSetupPromise
      return
    }
    accessSetupPromise = (async () => {
      try {
        const access = await navigator.requestMIDIAccess({ sysex: false })
        sharedAccess = access
        setSupported(true)
        const refresh = () => {
          const { inputs, outputs } = enumerateDevices(access)
          setHwDevices(inputs, outputs)
          access.inputs.forEach(input => ensureInputHandler(input, pushMessage))
        }
        refresh()
        access.onstatechange = refresh
      } catch {
        setSupported(false)
      }
    })()
    await accessSetupPromise
  }, [setSupported, setHwDevices, pushMessage])

  /**
   * Dispatch a MIDI message produced inside the app (virtual keyboard,
   * sequencer, future virtual sources) through the same routing
   * fan-out as a hardware input. `inputId` must correspond to one of
   * the entries in `VIRTUAL_INPUTS` so the router can route to it.
   */
  const dispatchVirtualMidi = useCallback(
    (inputId: string, data: number[], timestamp?: number) => {
      if (sharedAccess) {
        for (const route of sharedRoutesRef.current) {
          if (route.inputId !== inputId) continue
          sharedAccess.outputs.get(route.outputId)?.send(data)
        }
      }
      const virtualInput = VIRTUAL_INPUTS.find(v => v.id === inputId)
      const parsed = parseMidiMessage(new Uint8Array(data), timestamp)
      // Fan virtual MIDI into the global dispatcher so on-screen
      // sources (virtual keyboard) can drive mappable controls
      // for testing / accessibility without needing hardware.
      emitGlobalMidiIn({
        message: {
          ...parsed,
          inputId,
          inputName: virtualInput?.name ?? inputId,
        },
        inputId,
      })
      // Same monitor gating as the hardware path — only push when
      // this virtual input is the focused one.
      if (sharedSelectedInputRef.current === inputId) {
        pushMessage({
          ...parsed,
          inputId,
          inputName: virtualInput?.name ?? inputId,
        })
      }
    },
    [pushMessage],
  )

  /**
   * Send a MIDI message directly to one hardware output — bypasses
   * the virtual-input broadcast fan-out. Used by the sequencer
   * when a pad has a specific `outputId` override so its hits go
   * *only* to the chosen device, not to every route that happens
   * to originate from the Sequencer virtual source.
   *
   * `outputId` must be a live `MIDIOutput.id` — resolve via the
   * `hwOutputs` reported by `getHwOutputs()` or via the
   * patchbay's routes. Silently no-ops if the output is gone
   * (device unplugged between scheduling and dispatch).
   */
  const dispatchToHardwareOutput = useCallback(
    (outputId: string, data: number[]) => {
      if (!sharedAccess) return
      sharedAccess.outputs.get(outputId)?.send(data)
    },
    [],
  )

  /**
   * Snapshot of the currently-routed hardware output ids for the
   * Sequencer virtual source, with display names from the
   * MIDIAccess map. Used by the pad-editor output dropdown so
   * the user picks among destinations they've already wired up
   * in the MIDI Router. Routes whose output has since been
   * unplugged are included but marked `available: false`.
   */
  const getSequencerRoutedOutputs = useCallback((): ReadonlyArray<{
    id: string
    name: string
    available: boolean
  }> => {
    if (!sharedAccess) return []
    const seen = new Set<string>()
    const out: Array<{ id: string; name: string; available: boolean }> = []
    for (const route of sharedRoutesRef.current) {
      if (route.inputId !== VIRTUAL_SEQUENCER_INPUT_ID) continue
      if (seen.has(route.outputId)) continue
      seen.add(route.outputId)
      const hw = sharedAccess.outputs.get(route.outputId)
      out.push({
        id: route.outputId,
        name: hw?.name ?? '(unavailable)',
        available: hw !== undefined,
      })
    }
    return out
  }, [])

  /**
   * Subscribe to note-on/off events routed into the virtual
   * Sequencer destination via the patchbay. Returns an unsubscribe
   * function. Typical use from SequencerPage:
   *
   *   useEffect(() => subscribeSequencerNoteIn(ev => {
   *     if (ev.isNoteOn) triggerPadByMidiNote(ev.note, ev.velocity)
   *   }), [subscribeSequencerNoteIn])
   */
  const subscribeSequencerNoteIn = useCallback(
    (listener: SequencerNoteInListener) => {
      sequencerNoteInListeners.add(listener)
      return () => {
        sequencerNoteInListeners.delete(listener)
      }
    },
    [],
  )

  /**
   * Subscribe to the global MIDI dispatch channel. Fires for EVERY
   * inbound message from any hardware input (note-on/off, CC,
   * pitchbend, program). The mapping provider uses this to look
   * up bindings and drive any mappable UI control, independent of
   * route configuration. Returns an unsubscribe function.
   */
  const subscribeGlobalMidiIn = useCallback(
    (listener: GlobalMidiInListener) => {
      globalMidiInListeners.add(listener)
      return () => {
        globalMidiInListeners.delete(listener)
      }
    },
    [],
  )

  return {
    requestAccess,
    dispatchVirtualMidi,
    dispatchToHardwareOutput,
    getSequencerRoutedOutputs,
    subscribeSequencerNoteIn,
    subscribeGlobalMidiIn,
  }
}
